import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check, Star } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { type: "spring", stiffness: 200, damping: 24, delay: 0.15 + i * 0.09 } }),
};

export default function PathHero({ image, badge, title, subtitle, cta, note, trust }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const words = title.split(" ");

  return (
    <section ref={ref} className="relative min-h-[92svh] w-full overflow-hidden bg-ink" data-testid="path-hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-br from-ink/85 via-ink/60 to-ink/40" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-8 min-h-[92svh] flex flex-col justify-end pb-14 pt-28">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-lg"
          data-testid="hero-badge"
        >
          <Check size={16} strokeWidth={2.5} />
          {badge}
        </motion.div>

        <h1 aria-label={title} className="font-serif font-semibold text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.03] tracking-tight max-w-4xl">
          {words.map((w, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]" aria-hidden="true">
              <motion.span className="inline-block" variants={line} custom={i} initial="hidden" animate="visible">
                {w}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-white/80 leading-relaxed"
        >
          {subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 240, damping: 24, delay: 0.75 }}
          className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <button
            onClick={() => scrollToId("form")}
            data-testid="hero-cta"
            className="h-14 px-8 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brandhover shadow-xl active:scale-95 transition-[transform,background-color]"
          >
            {cta}
          </button>
          <span className="text-sm text-white/70">{note}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex items-center gap-4 flex-wrap"
        >
          <div className="flex -space-x-2.5">
            {["#F05A00", "#1A1512", "#10B981", "#5C5754"].map((bg, i) => (
              <span key={i} className="h-9 w-9 rounded-full border-2 border-white/80" style={{ background: bg }} />
            ))}
          </div>
          <div className="flex flex-col">
            <div className="flex text-brand">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={15} fill="currentColor" strokeWidth={0} />)}
            </div>
            <span className="text-sm text-white/80 font-medium">{trust}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
